import { getToken, onMessage } from "firebase/messaging";
import { messagingApp } from "../firebase-config";
import * as apiService from "../api-service";

// Set in Firebase Console > Project Settings > Cloud Messaging > Web configuration > Web Push
// certificates (generate a key pair there if one doesn't already exist yet), then added as
// REACT_APP_FIREBASE_VAPID_KEY in whichever .env file this app is built with. There is no way to
// read this back from the SDK itself - it has to be supplied this way, and every environment that
// builds this app (dev/prod) needs it set or push notifications simply won't work in that build.
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Registered at its own scope (see public/firebase-messaging-sw.js's own comment) so it doesn't
// fight the app's own CRA/Workbox service worker over control of the root "/" scope.
const PUSH_SW_URL = "/firebase-messaging-sw.js";
const PUSH_SW_SCOPE = "/firebase-push/";

// Remembers this device's own current FCM token purely so it can be handed back to the
// unsubscribe endpoint later (e.g. on logout) without needing to mint/re-fetch a token at that
// point too - device-level, not tied to whoever's currently signed in on it, matching this app's
// existing convention for other device-only preferences (dark/light mode, etc).
const DEVICE_TOKEN_KEY = "pushNotificationDeviceToken";

// navigator.serviceWorker.register() resolves as soon as installation STARTS, not once the worker
// is actually active - it can still be "installing" or "waiting" at that point. getToken() below
// calls PushManager.subscribe() against whatever registration it's given, which requires an
// ACTIVE worker; calling it too early throws "AbortError: ...no active Service Worker". This
// waits for that transition before handing the registration back to anything else.
function waitForServiceWorkerActive(registration) {
  if (registration.active) {
    return Promise.resolve(registration);
  }
  const worker = registration.installing ?? registration.waiting;
  if (!worker) {
    // No worker to wait on at all (shouldn't normally happen right after register() resolves) -
    // nothing further to do here, so hand the registration back as-is rather than hanging forever.
    return Promise.resolve(registration);
  }
  return new Promise((resolve) => {
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") {
        resolve(registration);
      }
    });
  });
}

let cachedRegistration = null;
async function getPushServiceWorkerRegistration() {
  if (!cachedRegistration) {
    const registration = await navigator.serviceWorker.register(PUSH_SW_URL, { scope: PUSH_SW_SCOPE });
    await waitForServiceWorkerActive(registration);
    cachedRegistration = registration;
  }
  return cachedRegistration;
}

// False for any browser/context that can't do web push at all (some in-app webviews, non-HTTPS
// origins other than localhost), or if REACT_APP_FIREBASE_VAPID_KEY hasn't been configured - in
// either case there's nothing this feature can do, so callers should just not offer it at all
// rather than letting someone hit a confusing error after clicking "Enable notifications".
export async function isPushSupported() {
  if (!VAPID_KEY || !("serviceWorker" in navigator) || typeof Notification === "undefined") {
    return false;
  }
  const messaging = await messagingApp;
  return messaging !== null;
}

// Prompts the browser's own native permission dialog, then - only if granted - registers this
// device with FCM and tells the backend which topic(s) to subscribe it to (see
// functions/express/routes/notifications/subscribe-topics.js; topic choice is based on this
// person's own extra roles, read server-side off their verified ID token, not anything sent up
// from here).
export async function enablePushNotifications({ userIdToken, companyId }) {
  const messaging = await messagingApp;
  if (!messaging) {
    return { granted: false, supported: false };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { granted: false, supported: true };
  }

  const registration = await getPushServiceWorkerRegistration();
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  if (!token) {
    return { granted: true, supported: true, token: null };
  }

  window.localStorage.setItem(DEVICE_TOKEN_KEY, token);
  await apiService.subscribeToPostNotifications({ userIdToken, companyId, token });
  return { granted: true, supported: true, token };
}

// Topic subscriptions are decided by the roles someone holds at the moment they subscribe, so a
// device that subscribed before a role was added to (or removed from) that person would otherwise
// keep the old set forever. App.js calls this whenever the signed-in person's roles change, to
// bring this device's subscriptions back in line - a no-op unless notifications were already
// enabled on this device, and never throws (best-effort, like logout below).
export async function syncPushSubscriptions({ userIdToken, companyId }) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }
  const token = window.localStorage.getItem(DEVICE_TOKEN_KEY);
  if (!token) {
    return;
  }
  try {
    await apiService.subscribeToPostNotifications({ userIdToken, companyId, token });
  } catch (err) {
    console.error("Failed to sync push notification subscriptions", err);
  }
}

// Called on logout (see AppNavBar.js) - best-effort, and deliberately never allowed to block
// signing out over a notification-plumbing failure. Unsubscribes this device's token from every
// possible topic unconditionally rather than re-checking claims, since someone's roles could have
// changed since this device last subscribed.
export async function disablePushNotifications({ userIdToken, companyId }) {
  const token = window.localStorage.getItem(DEVICE_TOKEN_KEY);
  if (!token) {
    return;
  }
  try {
    await apiService.unsubscribeFromPostNotifications({ userIdToken, companyId, token });
  } catch (err) {
    console.error("Failed to unsubscribe device from push notifications", err);
  } finally {
    window.localStorage.removeItem(DEVICE_TOKEN_KEY);
  }
}

// A push that arrives while this tab already has focus isn't shown as a native OS notification -
// that's deliberate browser/FCM behaviour (only a background/unfocused tab's service worker shows
// one), not a bug. This is how a foreground tab finds out a post just went live, so it can show
// its own in-app toast instead (see App.js).
export function listenForForegroundPush(onReceived) {
  messagingApp.then((messaging) => {
    if (messaging) {
      onMessage(messaging, onReceived);
    }
  });
}
